import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsIndexPage() {
  usePageMeta(
    "Events & Battles",
    "Battles, cyphers, showcases and festivals across the UK. Browse upcoming USDD Academy events."
  );
  const [events, setEvents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Events & Battles"
          title="Where the"
          highlight="culture collides."
          description="Battles, cyphers, showcases and festivals — run by dancers, for dancers. Browse upcoming events and register."
          ctaPrimary={{ label: "Register for Battles", to: "/contact?focus=battles&intent=register" }}
          ctaSecondary={{ label: "See pricing", to: "/pricing" }}
          bg={SITE.battle}
        />
        <section data-focus="battles" data-testid="events-focus-target" className="py-16 sm:py-24 bg-black">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((e, i) => (
                <motion.article
                  key={e.id}
                  data-testid={`event-card-${e.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-brand-yellow/50 bg-zinc-950/60 transition-all hover:-translate-y-1"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={e.image || SITE.battle}
                      alt={e.title}
                      className="absolute inset-0 w-full h-full object-cover brightness-[0.55] group-hover:brightness-75 group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    {e.featured && (
                      <span className="absolute top-3 left-3 text-[10px] font-black uppercase tracking-[0.25em] bg-brand-yellow text-black px-2.5 py-1 rounded-full glow-yellow">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{e.title}</h3>
                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed line-clamp-2">{e.excerpt}</p>
                    <div className="mt-4 flex items-center gap-4 text-[11px] text-zinc-500 uppercase tracking-[0.2em]">
                      {e.venue && <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {e.venue}</span>}
                      {e.starts_at && <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> {new Date(e.starts_at).toLocaleDateString()}</span>}
                    </div>
                    <Link
                      to={`/events/${e.slug}`}
                      className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow hover:gap-3 transition-all"
                    >
                      Register / View <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </motion.article>
              ))}
              {events.length === 0 && (
                <p className="text-zinc-400 col-span-full">No events scheduled right now — check back soon.</p>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
